import { DateTime } from 'luxon';

export function formatDate(date, timezone) {
  if (date) {
    // Parse in the target zone rather than converting into it: a date-only
    // value (e.g. '2026-10-27') would otherwise be read as midnight in the
    // browser's zone and shift to the previous day for any timezone west of
    // it (e.g. browser in Spain, profile timezone America/Phoenix).
    return DateTime.fromISO(date, { zone: timezone }).toLocaleString(DateTime.DATE_MED);
  }

  return '(not provided)';
}

export function formatDateRange(startDate, endDate, timezone) {
  if (startDate && endDate) {
    return `${formatDate(startDate, timezone)} - ${formatDate(endDate, timezone)}`;
  }

  return '(not provided)';
}

export function formatTime(time, timezone, includeTimezone = false) {
  if (time) {
    return includeTimezone ?
        DateTime.fromISO(time).setZone(timezone).toLocaleString(DateTime.TIME_SIMPLE_WITH_SHORT_OFFSET) :
        DateTime.fromISO(time).setZone(timezone).toLocaleString(DateTime.TIME_SIMPLE);
  }

  return '(not provided)';
}

export function formatTimeRange(startTime, endTime, timezone) {
  if (startTime && endTime) {
    return `${formatTime(startTime, timezone)} - ${formatTime(endTime, timezone, true)}`;
  }

  return '(not provided)';
}

export function getTimestampDiff(startTime, endTime) {
  return DateTime.fromISO(startTime).diff(DateTime.fromISO(endTime)).milliseconds;
}

/**
 * Number of whole UTC calendar days a date-only value (e.g. a project,
 * offer, or event endDate) has been in the past. 0 means it expires today
 * (in UTC); a negative number means it's still upcoming.
 *
 * The API stores these fields as a plain SQL `date` (no time, no timezone),
 * and the queries that filter on them compare against "now" by truncating
 * to a UTC calendar date. This needs to use that same UTC calendar date,
 * or a resource can show as expired here while still being treated as
 * active by a list that queries the API (or the reverse).
 *
 * @param {string|null|undefined} date - ISO 8601 date string.
 * @returns {number} Number of whole UTC calendar days the date has been in the past, or -Infinity if no date is provided.
 */
export function getDaysExpired(date) {
  if (!date) {
    return -Infinity;
  }

  const endDate = DateTime.fromISO(date, { zone: 'utc' }).startOf('day');
  const today = DateTime.utc().startOf('day');

  return today.diff(endDate, 'days').days;
}

/**
 * A resource is still active through the entirety of its end date (that's
 * the day someone has until), and only counts as expired once "today" (in
 * UTC) is a later calendar date than that — i.e. a strictly positive
 * getDaysExpired, not >= 0. That also matches the API's own `endDate >=
 * now` filter, which only excludes a resource starting the day after.
 *
 * @param {string|null|undefined} date - ISO 8601 date string.
 * @returns {boolean} True if the date has expired (calendar day is in the past in UTC), false otherwise.
 */
export function isExpired(date) {
  return getDaysExpired(date) > 0;
}
